import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ReactMarkdown from "react-markdown";
import "./EventModal.css";
import DurationPresets from "./DurationPresets";
import useProjects from "../hooks/useProjects";
import ProjectPicklist from "./ProjectPicklist";
import { Toggle } from "@fluentui/react";
import { beginFreshbooksConnect } from "../api/authApi";
import { downloadAttachment } from "../api/attachmentsApi";
import {
  getAttachmentDisplayName,
  isUploadedAttachment,
  normalizeEventAttachments,
} from "../utils/eventAttachments";

const getDatePart = (iso) => (iso ? iso.slice(0, 10) : "");
const getTimePart = (iso) => (iso ? iso.slice(11, 16) : "");
const isValidHHmm = (value) => /^\d{2}:\d{2}$/.test(value || "");
const pad2 = (n) => String(n).padStart(2, "0");
const normalizeTimeFormat = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .startsWith("24")
    ? "24h"
    : "12h";
const formatDateWithWeekday = (value) => {
  if (!value) return "Select date";

  const dateOnly = String(value).slice(0, 10);
  const parts = dateOnly.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return dateOnly;
  }

  const [year, month, day] = parts;
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(localDate.getTime())) return dateOnly;

  const weekday = localDate.toLocaleDateString("en-GB", { weekday: "long" });
  const monthLabel = localDate.toLocaleDateString("en-GB", { month: "short" });
  return `${weekday}  ${day} ${monthLabel} ${year}`;
};
const formatWeekdayLabel = (value, timeFormat = "12h") => {
  if (!value) return "Day";

  const dateOnly = String(value).slice(0, 10);
  const parts = dateOnly.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return "Day";
  }

  const [year, month, day] = parts;
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(localDate.getTime())) return "Day";
  return localDate.toLocaleDateString("en-GB", {
    weekday: normalizeTimeFormat(timeFormat) === "24h" ? "long" : "short",
  });
};
const formatDateLabel = (value) => {
  if (!value) return "Select date";

  const dateOnly = String(value).slice(0, 10);
  const parts = dateOnly.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return dateOnly;
  }

  const [year, month, day] = parts;
  const localDate = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (Number.isNaN(localDate.getTime())) return dateOnly;

  const monthLabel = localDate.toLocaleDateString("en-GB", { month: "short" });
  return `${day} ${monthLabel} ${year}`;
};
const toLocalDateTimeValue = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}`;
const toHHmm = (date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
const formatTimeForDisplay = (value, timeFormat = "12h") => {
  const match = /^(\d{2}):(\d{2})$/.exec(value || "");
  if (!match) return value || "--:--";

  const hour24 = Number(match[1]);
  const minute = match[2];
  if (timeFormat === "24h") {
    return `${String(hour24).padStart(2, "0")}:${minute}`;
  }

  const suffix = hour24 >= 12 ? "pm" : "am";
  const hour12 = hour24 % 12 || 12;
  return `${hour12}:${minute}${suffix}`;
};

// ─── Time selects (no keyboard, 5-minute slots) ───────────────────────────
function renderTimeSelects(value, onChange, timeFormat = "12h") {
  const match = /^(\d{2}):(\d{2})$/.exec(value || "");
  const hour24 = match ? parseInt(match[1], 10) : 9;
  const rawMinute = match ? parseInt(match[2], 10) : 0;
  const minute = Math.round(rawMinute / 5) * 5 % 60;
  const isPm = hour24 >= 12;
  const hour12 = hour24 % 12 || 12;

  const emit = (h12, pm, m) => {
    const h24 = h12 % 12 + (pm ? 12 : 0);
    onChange(`${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  };

  if (timeFormat === "24h") {
    return (
      <div className="time-selects">
        <select
          value={hour24}
          onChange={(e) => onChange(`${String(Number(e.target.value)).padStart(2, "0")}:${String(minute).padStart(2, "0")}`)}
        >
          {Array.from({ length: 24 }, (_, hour) => hour).map((h) => (
            <option key={h} value={h}>{String(h).padStart(2, "0")}</option>
          ))}
        </select>
        <span className="time-selects-sep">:</span>
        <select
          value={minute}
          onChange={(e) => onChange(`${String(hour24).padStart(2, "0")}:${String(Number(e.target.value)).padStart(2, "0")}`)}
        >
          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
            <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className="time-selects">
      <select
        value={hour12}
        onChange={(e) => emit(Number(e.target.value), isPm, minute)}
      >
        {[12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="time-selects-sep">:</span>
      <select
        value={minute}
        onChange={(e) => emit(hour12, isPm, Number(e.target.value))}
      >
        {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
        ))}
      </select>
      <select
        value={isPm ? "pm" : "am"}
        onChange={(e) => emit(hour12, e.target.value === "pm", minute)}
      >
        <option value="am">AM</option>
        <option value="pm">PM</option>
      </select>
    </div>
  );
}

const getMonthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const toDateOnly = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

const buildCalendarCells = (visibleMonth) => {
  const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1);
  const firstWeekday = (firstDay.getDay() + 6) % 7; // Monday first
  const gridStart = new Date(firstDay);
  gridStart.setDate(firstDay.getDate() - firstWeekday);

  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const cellDate = new Date(gridStart);
    cellDate.setDate(gridStart.getDate() + i);
    cells.push(cellDate);
  }
  return cells;
};

const parseDateOnly = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDateOnlyForUi = (value) => {
  const dt = parseDateOnly(value);
  if (!dt) return String(value || "");
  return dt.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

const repeatRuleUnit = (rule) => {
  if (rule === "daily") return "days";
  if (rule === "yearly_date") return "years";
  if (String(rule).startsWith("weekly_")) return "weeks";
  if (String(rule).startsWith("monthly_")) return "months";
  return "periods";
};

const weekdayIndexUtc = (date) => {
  const jsDay = date.getUTCDay();
  return jsDay === 0 ? 6 : jsDay - 1;
};

const monthNthWeekday = (date) => Math.floor((date.getUTCDate() - 1) / 7) + 1;

const dateOnlyUtc = (date) =>
  `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;

const addMonthsClampedUtc = (date, months, anchorDay) => {
  const shifted = new Date(date);
  shifted.setUTCDate(1);
  shifted.setUTCMonth(shifted.getUTCMonth() + months);
  const monthLastDay = new Date(
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0)
  ).getUTCDate();
  shifted.setUTCDate(Math.min(anchorDay, monthLastDay));
  return shifted;
};

const monthlyNthWeekdayUtc = (year, monthIndex, nth, weekday) => {
  const dayCount = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const matches = [];
  for (let day = 1; day <= dayCount; day += 1) {
    const d = new Date(Date.UTC(year, monthIndex, day));
    if (weekdayIndexUtc(d) === weekday) matches.push(day);
  }
  if (!matches.length) return new Date(Date.UTC(year, monthIndex, 1));
  const idx = Math.max(0, Math.min(matches.length - 1, nth - 1));
  return new Date(Date.UTC(year, monthIndex, matches[idx]));
};

const nextRepeatDateUtc = (currentDate, startDate, rule) => {
  const next = new Date(currentDate);
  if (rule === "daily") {
    next.setUTCDate(next.getUTCDate() + 1);
    return next;
  }

  if (String(rule).startsWith("weekly_")) {
    next.setUTCDate(next.getUTCDate() + 7);
    return next;
  }

  if (rule === "monthly_specific_day") {
    return addMonthsClampedUtc(next, 1, startDate.getUTCDate());
  }

  if (rule === "monthly_edge_first") {
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + 1);
    return next;
  }

  if (rule === "monthly_edge_last") {
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + 2);
    next.setUTCDate(0);
    return next;
  }

  if (rule === "monthly_nth_weekday") {
    const nth = monthNthWeekday(startDate);
    const weekday = weekdayIndexUtc(startDate);
    const nextMonth = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 1));
    return monthlyNthWeekdayUtc(nextMonth.getUTCFullYear(), nextMonth.getUTCMonth(), nth, weekday);
  }

  if (rule === "yearly_date") {
    next.setUTCFullYear(next.getUTCFullYear() + 1);
    return next;
  }

  next.setUTCDate(next.getUTCDate() + 1);
  return next;
};

const repeatDateMatchesRule = (startDateOnly, candidateDateOnly, rule) => {
  const startDate = parseDateOnly(startDateOnly);
  const candidateDate = parseDateOnly(candidateDateOnly);
  if (!startDate || !candidateDate) return false;
  if (candidateDate < startDate) return false;

  let cursor = new Date(startDate);
  for (let i = 0; i < 520; i += 1) {
    const current = dateOnlyUtc(cursor);
    if (current === candidateDateOnly) return true;
    if (current > candidateDateOnly) return false;
    cursor = nextRepeatDateUtc(cursor, startDate, rule);
  }
  return false;
};

const buildRepeatPreviewDates = (startDateOnly, rule, count = 4) => {
  const startDate = parseDateOnly(startDateOnly);
  if (!startDate) return [];

  const out = [];
  let cursor = new Date(startDate);
  for (let i = 0; i < count; i += 1) {
    out.push(dateOnlyUtc(cursor));
    cursor = nextRepeatDateUtc(cursor, startDate, rule);
  }

  return out;
};

const REPEAT_PATTERN_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekly_specific_day", label: "Specific weekday every week" },
  { value: "weekly_edge_first", label: "First day of each week" },
  { value: "weekly_edge_last", label: "Last day of each week" },
  { value: "monthly_specific_day", label: "Specific day of each month" },
  { value: "monthly_edge_first", label: "First day of each month" },
  { value: "monthly_edge_last", label: "Last day of each month" },
  { value: "monthly_nth_weekday", label: "Nth weekday of each month" },
  { value: "yearly_date", label: "Same date each year" },
];

const REPEAT_END_MODE_OPTIONS = [
  { value: "frequency", label: "Frequency" },
  { value: "until", label: "Date until" },
  { value: "forever", label: "Forever" },
];

const WEEKDAY_OPTIONS = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 0, label: "Sunday" },
];

const nextWeekdayDateOnly = (startDateOnly, jsWeekday) => {
  const dt = new Date(`${startDateOnly}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return startDateOnly;
  const current = dt.getDay();
  const delta = (Number(jsWeekday) - current + 7) % 7;
  dt.setDate(dt.getDate() + delta);
  return toDateOnly(dt);
};

const normalizedPatternStartDateOnly = (startDateOnly, rule) => {
  if (!startDateOnly || !rule || rule === "daily") return startDateOnly;

  const startDate = parseDateOnly(startDateOnly);
  if (!startDate) return startDateOnly;

  const next = new Date(startDate);
  const jsDay = next.getUTCDay();

  if (rule === "weekly_edge_first") {
    const delta = (8 - (jsDay || 7)) % 7;
    next.setUTCDate(next.getUTCDate() + delta);
    return dateOnlyUtc(next);
  }

  if (rule === "weekly_edge_last") {
    const delta = (7 - jsDay + 7) % 7;
    next.setUTCDate(next.getUTCDate() + delta);
    return dateOnlyUtc(next);
  }

  if (rule === "monthly_edge_first") {
    if (next.getUTCDate() === 1) return startDateOnly;
    next.setUTCDate(1);
    next.setUTCMonth(next.getUTCMonth() + 1);
    return dateOnlyUtc(next);
  }

  if (rule === "monthly_edge_last") {
    const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
    if (next.getUTCDate() === lastDay) return startDateOnly;
    next.setUTCDate(lastDay);
    return dateOnlyUtc(next);
  }

  return startDateOnly;
};

const REMINDER_OFFSET_OPTIONS = [
  { value: 0, label: "At event time" },
  { value: 5, label: "5 minutes before" },
  { value: 15, label: "15 minutes before" },
  { value: 30, label: "30 minutes before" },
  { value: 60, label: "1 hour before" },
  { value: 180, label: "3 hours before" },
  { value: 1440, label: "1 day before" },
  { value: 10080, label: "1 week before" },
];

const birthdayTitleRegex = /\b(birthday|b[\s-]?day)\b/i;
const anniversaryTitleRegex = /\b(anniversary|anniversaries|anniversay)\b/i;

function getCelebrationIntent(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  if (birthdayTitleRegex.test(text)) return "birthday";
  if (anniversaryTitleRegex.test(text)) return "anniversary";
  return "";
}

function findCategoryForIntent(categories, intent) {
  if (!Array.isArray(categories) || !intent) return null;

  const birthdayCategoryRegex = /\b(birthday|birthdays|b[\s-]?day|b[\s-]?days)\b/i;
  const anniversaryCategoryRegex = /\b(anniversary|anniversaries|anniversay)\b/i;
  const matcher = intent === "birthday" ? birthdayCategoryRegex : anniversaryCategoryRegex;

  return (
    categories.find((item) => matcher.test(String(item?.name || "").trim())) ||
    null
  );
}

const defaultReminderConfig = {
  offset_minutes: 15,
  reminder_date: "",
};

function parseReminderConfig(value, eventStart) {
  const fallbackDate = getDatePart(eventStart || "") || getDatePart(new Date().toISOString());
  const merged = {
    ...defaultReminderConfig,
    reminder_date: fallbackDate,
  };

  if (!value || typeof value !== "string") {
    return merged;
  }

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return merged;

    return {
      ...merged,
      ...parsed,
      reminder_date: String(parsed.reminder_date || parsed.one_time_date || merged.reminder_date),
      offset_minutes: Number(parsed.offset_minutes ?? merged.offset_minutes),
    };
  } catch {
    return merged;
  }
}

function reminderSummary(config, enabled) {
  if (!enabled) return "Reminders are off";

  const offsetLabel = REMINDER_OFFSET_OPTIONS.find(
    (item) => Number(item.value) === Number(config.offset_minutes)
  )?.label || "Custom offset";

  const dateText = formatDateOnlyForUi(config.reminder_date);
  return `${offsetLabel} • ${dateText || "No reminder date"}`;
}

function linkifyBareUrls(text) {
  if (!text) return "";

  return text.replace(/(?:https?:\/\/|www\.)[^\s<]+/g, (url, offset, fullText) => {
    const prefix = fullText.slice(Math.max(0, offset - 2), offset);

    // Avoid rewriting URLs already used in markdown link syntax: [label](url)
    if (prefix === "](") {
      return url;
    }

    const suffixMatch = url.match(/[).,!?:;]+$/);
    const trailing = suffixMatch ? suffixMatch[0] : "";
    const cleanUrl = trailing ? url.slice(0, -trailing.length) : url;
    const href = cleanUrl.startsWith("www.") ? `https://${cleanUrl}` : cleanUrl;

    return `[${cleanUrl}](${href})${trailing}`;
  });
}

function formatFileSize(bytes) {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / 104857.6) / 10} MB`;
}

function EventModal({
  isOpen,
  onClose,
  data,
  setData,
  isEdit,
  onSave,
  onDelete,
  onDeleteFollowing,
  onDeleteAll,
  isSaving,
  categories = [],
  timeFormat = "12h",
  onOpenCategoryManager,
}) {
  const effectiveTimeFormat = normalizeTimeFormat(timeFormat);
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [typedStart, setTypedStart] = useState("");
  const [typedEnd, setTypedEnd] = useState("");
  const [connectError, setConnectError] = useState("");
  const [connectingFreshbooks, setConnectingFreshbooks] = useState(false);
  const [notesMode, setNotesMode] = useState("edit");
  const [isRepeatManagerOpen, setIsRepeatManagerOpen] = useState(false);
  const [activeDateTimePanel, setActiveDateTimePanel] = useState(null);
  const [startCalendarMonth, setStartCalendarMonth] = useState(getMonthStart(new Date()));
  const [endCalendarMonth, setEndCalendarMonth] = useState(getMonthStart(new Date()));
  const [repeatUntilCalendarMonth, setRepeatUntilCalendarMonth] = useState(getMonthStart(new Date()));
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [locationLookupBusy, setLocationLookupBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  const titleRef = useRef(null);
  const internalNotesRef = useRef(null);
  const locationInputRef = useRef(null);
  const attachmentInputRef = useRef(null);
  const locationLookupTimerRef = useRef(null);
  const locationLookupAbortRef = useRef(null);
  const birthdayDefaultsAppliedRef = useRef(false);
  const celebrationCategoryAppliedRef = useRef(false);

  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
  } = useProjects();

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isOpen) return;

    setSaveAttempted(false);
    birthdayDefaultsAppliedRef.current = false;
    celebrationCategoryAppliedRef.current = false;

    if (data.start && getTimePart(data.start)) {
      setTypedStart(getTimePart(data.start));
    }

    if (data.end && getTimePart(data.end)) {
      setTypedEnd(getTimePart(data.end));
    }

    const isCoarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;

    // Avoid forced focus for edit/update flows and touch devices to prevent mobile keyboard pop-up.
    if (titleRef.current && !data.is_lesson && !isEdit && !isCoarsePointer) {
      titleRef.current.focus();
    }
    setNotesMode("edit");
    setIsRepeatManagerOpen(false);
    setActiveDateTimePanel(null);
    setPendingFiles([]);

    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = "";
    }

    const startDate = new Date(`${getDatePart(data.start) || getDatePart(new Date().toISOString())}T00:00:00`);
    const endDate = new Date(`${getDatePart(data.end) || getDatePart(new Date().toISOString())}T00:00:00`);
    const repeatUntilDate = new Date(`${getDatePart(data.repeat_until) || getDatePart(data.start) || getDatePart(new Date().toISOString())}T00:00:00`);
    if (!Number.isNaN(startDate.getTime())) {
      setStartCalendarMonth(getMonthStart(startDate));
    }
    if (!Number.isNaN(endDate.getTime())) {
      setEndCalendarMonth(getMonthStart(endDate));
    }
    if (!Number.isNaN(repeatUntilDate.getTime())) {
      setRepeatUntilCalendarMonth(getMonthStart(repeatUntilDate));
    }
  }, [isOpen, data.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ⭐ Populate modal fields from incoming data
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!isOpen) return;

    setData((prev) => ({
      ...prev,
      title: data.title || "",
      repeat_rule: data.repeat_rule === "forever" ? "weekly_specific_day" : data.repeat_rule || "none",
      repeat_until: data.repeat_until || "",
      repeat_interval: Number(data.repeat_interval || 1),
      repeat_end_mode:
        data.repeat_end_mode || (data.repeat_rule === "forever" ? "forever" : "frequency"),
      repeat_periods: Number(data.repeat_periods || 3),
      all_day: data.all_day ?? false,
      is_lesson: data.is_lesson ?? false,
      no_show: data.no_show ?? false,
      fb_time_entry_id: data.fb_time_entry_id ?? "",
      project_id: data.project_id ?? null,
      project_name: data.project_name ?? "",
      invoice_notes: data.invoice_notes ?? "",
      internal_notes: data.internal_notes ?? "",
      location: data.location ?? "",
      category_id: data.category_id ?? "",
      category_name: data.category_name ?? "",
      category_color: data.category_color ?? "",
      reminder_enabled: data.reminder_enabled ?? false,
      reminder_config: data.reminder_config ?? "",
      attachments: normalizeEventAttachments(data.attachments),
    }));
  }, [isOpen]);
  /* eslint-disable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!isOpen) return;
    if (!data.category_id) return;

    const selected = categories.find((item) => item.id === data.category_id);
    if (!selected) return;

    const nextName = selected.name || "";
    const nextColor = selected.color || "";
    if (data.category_name === nextName && data.category_color === nextColor) return;

    setData((prev) => {
      if ((prev.category_id || "") !== data.category_id) return prev;
      return {
        ...prev,
        category_name: nextName,
        category_color: nextColor,
      };
    });
  }, [
    isOpen,
    categories,
    data.category_id,
    data.category_name,
    data.category_color,
    setData,
  ]);

  useEffect(() => {
    if (!isOpen || isEdit) return;
    if (!data.all_day || data.is_lesson) return;
    const intent = getCelebrationIntent(data.title);
    if (!intent) return;

    const startDateOnly = getDatePart(data.start) || getDatePart(new Date().toISOString());
    const hasCustomRepeat = (data.repeat_rule || "none") !== "none";
    const hasReminderEnabled = data.reminder_enabled === true;

    if (!birthdayDefaultsAppliedRef.current) {
      const nextReminderConfig = (() => {
        const parsed = parseReminderConfig(data.reminder_config, data.start);
        return JSON.stringify({
          ...parsed,
          pattern: "one_time_date",
          offset_minutes: 10080,
          reminder_date: startDateOnly,
          one_time_date: startDateOnly,
        });
      })();

      setData((prev) => ({
        ...prev,
        repeat_rule: hasCustomRepeat ? prev.repeat_rule : "yearly_date",
        repeat_end_mode: hasCustomRepeat ? prev.repeat_end_mode : "forever",
        repeat_until: hasCustomRepeat ? prev.repeat_until : "",
        reminder_enabled: hasReminderEnabled ? prev.reminder_enabled : true,
        reminder_config: hasReminderEnabled && prev.reminder_config ? prev.reminder_config : nextReminderConfig,
      }));

      birthdayDefaultsAppliedRef.current = true;
    }

    if (celebrationCategoryAppliedRef.current) return;
    if (String(data.category_id || "").trim()) {
      celebrationCategoryAppliedRef.current = true;
      return;
    }

    const matchedCategory = findCategoryForIntent(categories, intent);
    if (!matchedCategory) return;

    setData((prev) => ({
      ...prev,
      category_id: String(matchedCategory.id || ""),
      category_name: String(matchedCategory.name || ""),
      category_color: String(matchedCategory.color || ""),
    }));

    celebrationCategoryAppliedRef.current = true;
  }, [
    isOpen,
    isEdit,
    data.all_day,
    data.is_lesson,
    data.title,
    data.start,
    data.repeat_rule,
    data.repeat_end_mode,
    data.repeat_until,
    data.reminder_enabled,
    data.reminder_config,
    data.category_id,
    categories,
    setData,
  ]);

  // Address suggestions (OpenStreetMap Nominatim) — works in both web and mobile.
  useEffect(() => {
    if (!isOpen || data.is_lesson) return;

    const query = String(data.location || "").trim();
    if (query.length < 3) {
      setLocationLookupBusy(false);
      setLocationSuggestions([]);
      return;
    }

    if (locationLookupTimerRef.current) {
      clearTimeout(locationLookupTimerRef.current);
      locationLookupTimerRef.current = null;
    }

    if (locationLookupAbortRef.current) {
      locationLookupAbortRef.current.abort();
      locationLookupAbortRef.current = null;
    }

    locationLookupTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      locationLookupAbortRef.current = controller;
      setLocationLookupBusy(true);

      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&countrycodes=gb&q=${encodeURIComponent(query)}`;
        const res = await fetch(url, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
          },
        });
        if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
        const rows = await res.json();
        const next = Array.isArray(rows)
          ? rows
              .map((item) => String(item?.display_name || "").trim())
              .filter(Boolean)
          : [];
        setLocationSuggestions(next);
      } catch (err) {
        if (err?.name !== "AbortError") {
          setLocationSuggestions([]);
        }
      } finally {
        setLocationLookupBusy(false);
        locationLookupAbortRef.current = null;
      }
    }, 280);

    return () => {
      if (locationLookupTimerRef.current) {
        clearTimeout(locationLookupTimerRef.current);
        locationLookupTimerRef.current = null;
      }
      if (locationLookupAbortRef.current) {
        locationLookupAbortRef.current.abort();
        locationLookupAbortRef.current = null;
      }
    };
  }, [isOpen, data.is_lesson, data.location]);

  // ⭐ Auto-expand internal notes
  useEffect(() => {
    if (internalNotesRef.current) {
      internalNotesRef.current.style.height = "auto";
      internalNotesRef.current.style.height =
        internalNotesRef.current.scrollHeight + "px";
    }
  }, [data.internal_notes]);

  const handleChange = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const getCurrentLocationValue = () => {
    const inputValue =
      typeof locationInputRef.current?.value === "string"
        ? locationInputRef.current.value.trim()
        : "";

    if (inputValue) return inputValue;
    return (data.location || "").trim();
  };

  const handleConnectFreshbooks = async () => {
    setConnectError("");
    setConnectingFreshbooks(true);
    try {
      await beginFreshbooksConnect(false);
    } catch (err) {
      setConnectError(err.message || "Could not start FreshBooks connection");
      setConnectingFreshbooks(false);
    }
  };

  const repeatEnabled = (data.repeat_rule || "none") !== "none";
  const reminderEnabled = data.reminder_enabled === true;
  const activeReminderConfig = parseReminderConfig(data.reminder_config, data.start);
  const repeatPattern = data.repeat_rule || "weekly_specific_day";
  const repeatEndMode = data.repeat_end_mode || (repeatPattern === "forever" ? "forever" : "frequency");
  const repeatPeriods = Math.max(1, Number(data.repeat_periods || 3));
  const repeatHasFiniteEnd = repeatEnabled && repeatEndMode !== "forever";
  const repeatNeedsUntil = repeatEnabled && repeatEndMode === "until";
  const repeatStartDateOnly = getDatePart(data.start);
  const isValidRepeatUntilDate = (dateOnly) => {
    if (!repeatHasFiniteEnd) return true;
    return repeatDateMatchesRule(repeatStartDateOnly, dateOnly, repeatPattern);
  };
  const repeatUntilDateIsValid = !data.repeat_until || isValidRepeatUntilDate(data.repeat_until);
  const repeatCalculatedUntil =
    repeatEnabled && repeatEndMode === "frequency"
      ? (buildRepeatPreviewDates(repeatStartDateOnly, repeatPattern, repeatPeriods).slice(-1)[0] || "")
      : "";
  const repeatWeekday = new Date(`${repeatStartDateOnly}T00:00:00`).getDay();
  const repeatWeekdayLabel =
    WEEKDAY_OPTIONS.find((item) => Number(item.value) === Number(repeatWeekday))?.label || "Day";

  const repeatSummaryText = repeatEnabled
    ? `${REPEAT_PATTERN_OPTIONS.find((item) => item.value === repeatPattern)?.label || "Pattern"} • ${repeatWeekdayLabel} • ${repeatEndMode === "forever" ? "Forever" : repeatEndMode === "until" ? `Until ${formatDateOnlyForUi(data.repeat_until || "")}` : repeatCalculatedUntil ? `Ends ${formatDateOnlyForUi(repeatCalculatedUntil)}` : "Frequency"}`
    : "Repeat is off";

  const applyEventStartDate = (dateOnly) => {
    const nextTime = typedStart || getTimePart(data.start) || "09:00";
    const oldStart = new Date(data.start);
    const oldEnd = new Date(data.end);
    const durationMs =
      !Number.isNaN(oldStart.getTime()) &&
      !Number.isNaN(oldEnd.getTime()) &&
      oldEnd.getTime() > oldStart.getTime()
        ? oldEnd.getTime() - oldStart.getTime()
        : 60 * 60000;
    const newStart = new Date(`${dateOnly}T${nextTime}`);
    if (Number.isNaN(newStart.getTime())) return;
    const newEnd = new Date(newStart.getTime() + durationMs);
    const endDate = newEnd.toISOString().split("T")[0];
    const hours = String(newEnd.getHours()).padStart(2, "0");
    const minutes = String(newEnd.getMinutes()).padStart(2, "0");
    const endTimeStr = `${hours}:${minutes}`;

    handleChange("start", `${dateOnly}T${nextTime}`);
    handleChange("end", `${endDate}T${endTimeStr}`);
    setEndCalendarMonth(getMonthStart(new Date(`${dateOnly}T00:00:00`)));
  };

  const handleRepeatToggle = (checked) => {
    if (checked) {
      const nextRule = data.repeat_rule && data.repeat_rule !== "none" ? data.repeat_rule : "weekly_specific_day";
      handleChange("repeat_rule", nextRule);
      if (!data.repeat_periods || Number(data.repeat_periods) < 1) {
        handleChange("repeat_periods", 3);
      }
      if (!data.repeat_end_mode) handleChange("repeat_end_mode", "frequency");
      return;
    }

    handleChange("repeat_rule", "none");
    handleChange("repeat_until", "");
    setIsRepeatManagerOpen(false);
  };

  const handleRepeatWeekdayChange = (weekdayValue) => {
    const normalizedStart = nextWeekdayDateOnly(repeatStartDateOnly, Number(weekdayValue));
    if (normalizedStart) {
      applyEventStartDate(normalizedStart);
    }
  };

  const updateReminderConfig = (changes) => {
    const nextConfig = {
      ...activeReminderConfig,
      ...changes,
      pattern: "one_time_date",
    };
    nextConfig.one_time_date = nextConfig.reminder_date || "";
    handleChange("reminder_config", JSON.stringify(nextConfig));
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!repeatEnabled) return;
    if (repeatEndMode === "forever") {
      if (data.repeat_until) {
        setData((prev) => ({ ...prev, repeat_until: "" }));
      }
      return;
    }
    if (repeatEndMode !== "frequency") return;
    if (!repeatCalculatedUntil) return;
    if (data.repeat_until === repeatCalculatedUntil) return;
    setData((prev) => ({ ...prev, repeat_until: repeatCalculatedUntil }));
  }, [
    isOpen,
    repeatEnabled,
    repeatPattern,
    repeatEndMode,
    repeatCalculatedUntil,
    data.repeat_until,
    setData,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (!repeatEnabled || repeatPattern === "daily") return;

    const normalized = normalizedPatternStartDateOnly(repeatStartDateOnly, repeatPattern);
    if (!normalized || normalized === repeatStartDateOnly) return;
    applyEventStartDate(normalized);
  }, [isOpen, repeatEnabled, repeatPattern, repeatStartDateOnly]);

  const getDurationSummary = () => {
    if (data.all_day) return "";

    const start = new Date(data.start);
    const end = new Date(data.end);
    const diff = (end - start) / 60000;

    if (isNaN(diff) || diff <= 0) return "";
    if (diff < 60) return `(${diff} minutes)`;
    if (diff === 60) return `(1 hour)`;
    if (diff % 60 === 0) return `(${diff / 60} hours)`;
    return `(${Math.floor(diff / 60)}h ${diff % 60}m)`;
  };

  const formatDateButtonLabel = (value) => {
    return formatDateLabel(value);
  };

  const toggleDateTimePanel = (panelName) => {
    if (panelName === "start-date") {
      const startDate = new Date(`${getDatePart(data.start) || getDatePart(new Date().toISOString())}T00:00:00`);
      if (!Number.isNaN(startDate.getTime())) {
        setStartCalendarMonth(getMonthStart(startDate));
      }
    }
    if (panelName === "end-date") {
      const endDate = new Date(`${getDatePart(data.end) || getDatePart(new Date().toISOString())}T00:00:00`);
      if (!Number.isNaN(endDate.getTime())) {
        setEndCalendarMonth(getMonthStart(endDate));
      }
    }
    if (panelName === "repeat-until-date") {
      const repeatDate = new Date(`${getDatePart(data.repeat_until) || getDatePart(data.start) || getDatePart(new Date().toISOString())}T00:00:00`);
      if (!Number.isNaN(repeatDate.getTime())) {
        setRepeatUntilCalendarMonth(getMonthStart(repeatDate));
      }
    }
    if (panelName === "repeat-start-date") {
      const repeatStartDate = new Date(`${getDatePart(data.start) || getDatePart(new Date().toISOString())}T00:00:00`);
      if (!Number.isNaN(repeatStartDate.getTime())) {
        setStartCalendarMonth(getMonthStart(repeatStartDate));
      }
    }
    setActiveDateTimePanel((prev) => (prev === panelName ? null : panelName));
  };

  const renderInlineCalendar = ({
    selectedDate,
    visibleMonth,
    onVisibleMonthChange,
    onSelectDate,
    minDate,
    isDateSelectable,
    highlightSelectableDates = false,
  }) => {
    const monthCells = buildCalendarCells(visibleMonth);
    const selectedDateOnly = selectedDate ? String(selectedDate).slice(0, 10) : "";
    const minDateOnly = minDate ? String(minDate).slice(0, 10) : "";
    const monthLabel = visibleMonth.toLocaleDateString("en-GB", {
      month: "long",
      year: "numeric",
    });

    return (
      <div className="event-calendar" role="group" aria-label="Calendar picker">
        <div className="event-calendar-header">
          <button
            type="button"
            className="event-calendar-nav"
            onClick={() =>
              onVisibleMonthChange(
                new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1)
              )
            }
          >
            ‹
          </button>
          <div className="event-calendar-title">{monthLabel}</div>
          <button
            type="button"
            className="event-calendar-nav"
            onClick={() =>
              onVisibleMonthChange(
                new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1)
              )
            }
          >
            ›
          </button>
        </div>

        <div className="event-calendar-weekdays">
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
          <span>Sun</span>
        </div>

        <div className="event-calendar-grid">
          {monthCells.map((cellDate) => {
            const dateOnly = toDateOnly(cellDate);
            const inMonth = cellDate.getMonth() === visibleMonth.getMonth();
            const selected = selectedDateOnly === dateOnly;
            const selectableByRule = isDateSelectable ? isDateSelectable(dateOnly) : true;
            const blocked = Boolean((minDateOnly && dateOnly < minDateOnly) || !selectableByRule);
            const className = [
              "event-calendar-day",
              inMonth ? "" : "event-calendar-day--other",
              selected ? "event-calendar-day--selected" : "",
              highlightSelectableDates && inMonth && selectableByRule ? "event-calendar-day--valid-slot" : "",
              highlightSelectableDates && inMonth && !selectableByRule ? "event-calendar-day--invalid-slot" : "",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={dateOnly}
                type="button"
                className={className}
                onClick={() => {
                  if (blocked) return;
                  onSelectDate(dateOnly);
                }}
                disabled={blocked}
              >
                {cellDate.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleStartTimeChange = (t) => {
    setTypedStart(t);
    const date = getDatePart(data.start);
    if (!isValidHHmm(t)) {
      handleChange("start", `${date}T${t}`);
      return;
    }

    const currentStart = new Date(data.start);
    const currentEnd = new Date(data.end);
    const durationMs =
      !Number.isNaN(currentStart.getTime()) &&
      !Number.isNaN(currentEnd.getTime()) &&
      currentEnd > currentStart
        ? currentEnd.getTime() - currentStart.getTime()
        : 60 * 60000;

    const nextStart = new Date(`${date}T${t}`);
    if (Number.isNaN(nextStart.getTime())) {
      handleChange("start", `${date}T${t}`);
      return;
    }

    const nextEnd = new Date(nextStart.getTime() + durationMs);
    const nextStartValue = toLocalDateTimeValue(nextStart);
    const nextEndValue = toLocalDateTimeValue(nextEnd);

    handleChange("start", nextStartValue);
    handleChange("end", nextEndValue);
    setTypedEnd(toHHmm(nextEnd));
  };

  const handleEndTimeChange = (t) => {
    setTypedEnd(t);
    const date = getDatePart(data.end);
    handleChange("end", `${date}T${t}`);
  };

  const titleMissing =
    !data.is_lesson && (!data.title || data.title.trim() === "");

  const projectMissing =
    data.is_lesson && !data.project_id;

  const titleInvalid = saveAttempted && titleMissing;
  const projectInvalid = saveAttempted && projectMissing;
  const celebrationIntent = getCelebrationIntent(data.title);
  const celebrationMatchedCategory = findCategoryForIntent(categories, celebrationIntent);
  const showCelebrationHint = !isEdit && !data.is_lesson && data.all_day && Boolean(celebrationIntent);
  const celebrationLabel = celebrationIntent === "anniversary" ? "Anniversary" : "Birthday";

  const timesInvalid = data.all_day
    ? false
    : data.start && data.end
    ? new Date(data.start) >= new Date(data.end)
    : false;

  // ⭐ CLICK-OUTSIDE-TO-CLOSE HANDLER
  useEffect(() => {
    if (!isOpen || data?.all_day !== true) return;
    const start = String(data?.start || "").trim();
    const end = String(data?.end || "").trim();
    if (!start || !end) return;

    // YYYY-MM-DD compares lexicographically in chronological order.
    if (end < start) {
      setData((prev) => {
        if (!prev || prev.all_day !== true) return prev;
        const prevStart = String(prev.start || "").trim();
        const prevEnd = String(prev.end || "").trim();
        if (!prevStart || !prevEnd || prevEnd >= prevStart) return prev;
        return { ...prev, end: prevStart };
      });
    }
  }, [isOpen, data?.all_day, data?.start, data?.end, setData]);

  const handleOverlayClick = (e) => {
    if (isSaving) return;
    if (e.target.classList.contains("modal-overlay")) {
      setSaveAttempted(false);
      onClose();
    }
  };

  const handlePrimarySave = () => {
    setSaveAttempted(true);

    const resolvedLocation = getCurrentLocationValue();
    if (resolvedLocation !== (data.location || "")) {
      setData((prev) => ({ ...prev, location: resolvedLocation }));
    }

    if (titleMissing) return;
    if (projectMissing) return;
    if (timesInvalid) return;

    const start = new Date(data.start);
    const end = new Date(data.end);
    const diffMinutes = (end - start) / 60000;

    if (data.is_lesson) {
      const startMinutes = start.getMinutes();
      const endMinutes = end.getMinutes();

      // HARD BLOCK: must be 5-minute increments
      if (startMinutes % 5 !== 0 || endMinutes % 5 !== 0) {
        window.alert(
          "Lesson start and end times must be set in 5-minute increments."
        );
        return;
      }

      // SOFT WARNING: not on quarter-hour (but still 5-minute aligned)
      if (
        startMinutes % 15 !== 0 ||
        endMinutes % 15 !== 0
      ) {
        const proceedQuarter = window.confirm(
          "This lesson is not aligned to a quarter-hour (e.g. 9:00, 9:15, 9:30, 9:45).\n\nDo you want to continue anyway?"
        );
        if (!proceedQuarter) return;
      }

      // SOFT WARNING: spans midnight
      if (end.toDateString() !== start.toDateString()) {
        const proceedMidnight = window.confirm(
          "This lesson spans midnight (multiple days).\n\nIs this intentional?"
        );
        if (!proceedMidnight) return;
      }

      // SOFT WARNING: exceeds 2 hours
      if (diffMinutes > 120) {
        const proceedLong = window.confirm(
          "This lesson exceeds 2 hours.\n\nIs this correct?"
        );
        if (!proceedLong) return;
      }
    }

    if ((data.repeat_rule || "none") !== "none") {
      const startDateOnly = getDatePart(data.start);
      const untilDateOnly = String(data.repeat_until || "");

      if (repeatEndMode !== "forever") {
        if (repeatEndMode === "until" && !untilDateOnly) {
          window.alert("Please choose a Repeat until date.");
          return;
        }
        if (untilDateOnly && untilDateOnly < startDateOnly) {
          window.alert("Repeat until date cannot be earlier than event start date.");
          return;
        }
        if (repeatEndMode === "until" && untilDateOnly && !isValidRepeatUntilDate(untilDateOnly)) {
          window.alert(
            "Please pick a valid Repeat until date that matches the selected pattern and frequency."
          );
          return;
        }
      }
    }

    if (reminderEnabled && !activeReminderConfig.reminder_date) {
      window.alert("Please choose a reminder date.");
      return;
    }

    onSave(resolvedLocation, pendingFiles);
  };

  const handleChooseAttachments = () => {
    attachmentInputRef.current?.click();
  };

  const handleAttachmentPickerChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setPendingFiles((prev) => [...prev, ...files]);
    event.target.value = "";
  };

  const removeAttachment = (index) => {
    setData((prev) => {
      const current = Array.isArray(prev.attachments) ? [...prev.attachments] : [];
      return { ...prev, attachments: current.filter((_, itemIndex) => itemIndex !== index) };
    });
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const openAttachment = async (item) => {
    if (!item) return;

    if (isUploadedAttachment(item)) {
      try {
        await downloadAttachment(item);
      } catch (err) {
        window.alert(`Could not open attachment:\n${err.message || err}`);
      }
      return;
    }

    const targetUrl = String(item.url || "").trim();
    if (!targetUrl) return;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="event-modal-header">
          <button
            type="button"
            className="event-modal-top-btn"
            onClick={() => {
              if (isSaving) return;
              setSaveAttempted(false);
              onClose();
            }}
            disabled={isSaving}
            aria-label="Close form"
          >
            X
          </button>
          <h2>{isEdit ? "Edit Event" : "New Event"}</h2>
          <button
            type="button"
            className="event-modal-top-btn event-modal-top-btn--save"
            onClick={handlePrimarySave}
            disabled={isSaving}
            aria-label={isEdit ? "Apply event changes" : "Apply new event"}
          >
            ✓
          </button>
        </div>

        {/* TITLE OR PROJECT */}
        {!data.is_lesson && (
          <>
            <label>Title*</label>
            <input
              ref={titleRef}
              type="text"
              value={data.title || ""}
              onChange={(e) => handleChange("title", e.target.value)}
              className={titleInvalid ? "invalid" : ""}
            />

            {showCelebrationHint ? (
              <div className="event-intent-hint">
                Detected {celebrationLabel}: yearly repeat and reminders defaulted.
                {celebrationMatchedCategory ? ` Category: ${celebrationMatchedCategory.name}.` : ""}
              </div>
            ) : null}

            {titleInvalid && (
              <div className="error-text">Title cannot be empty</div>
            )}
          </>
        )}

        {data.is_lesson && (
          <>
            <label>Project*</label>

            {projectsLoading ? (
              <div style={{ opacity: 0.6 }}>Loading projects…</div>
            ) : (
              <>
                {projectsError ? (
                  <>
                    <div className="error-text">{projectsError}</div>
                    <button
                      type="button"
                      className="connect-freshbooks-btn"
                      onClick={handleConnectFreshbooks}
                      disabled={connectingFreshbooks}
                    >
                      {connectingFreshbooks ? "Connecting..." : "Connect FreshBooks"}
                    </button>
                    {connectError ? <div className="error-text">{connectError}</div> : null}
                  </>
                ) : null}

                <div className={projectInvalid ? "invalid" : ""}>
                  <ProjectPicklist
                    projects={projects}
                    value={data.project_id || null}
                    onChange={(p) => {
                      handleChange("project_id", String(p.id));
                      handleChange("project_name", p.name);

                      const fullLabel = p.client
                        ? `${p.name} (${p.client})`
                        : p.name;

                      handleChange("title", fullLabel);
                    }}
                  />
                </div>
              </>
            )}

            {projectInvalid && (
              <div className="error-text">Please select a project</div>
            )}
          </>
        )}

        {/* ⭐ LESSON / ALL DAY TOGGLES */}
        <div className="toggle-row">
          <div className="toggle-row-left">
            <Toggle
              checked={data.is_lesson || false}
              onChange={(e, checked) => {
                handleChange("is_lesson", checked);

                if (checked) {
                  handleChange("all_day", false);
                  handleChange("location", "Online");
                  setSaveAttempted(true);
                } else {
                  handleChange("project_id", null);
                  handleChange("project_name", null);
                  handleChange("no_show", false);
                  handleChange("title", "");
                  handleChange("location", "");
                }
              }}
              label="Lesson"
              inlineLabel
            />

            {data.is_lesson && (
              <Toggle
                checked={data.no_show || false}
                onChange={(e, checked) => handleChange("no_show", checked)}
                label="No Show"
                inlineLabel
              />
            )}
          </div>

          <div className="toggle-row-right">
            {!data.is_lesson && (
              <Toggle
                checked={data.all_day || false}
                onChange={(e, checked) => {
                  handleChange("all_day", checked);
                  if (checked) {
                    setActiveDateTimePanel(null);
                    handleChange("is_lesson", false);
                    handleChange("no_show", false);
                    handleChange("project_id", null);
                    handleChange("project_name", null);
                    const dateOnly =
                      getDatePart(data.start) || new Date().toISOString().slice(0, 10);
                    handleChange("start", dateOnly);
                    handleChange("end", dateOnly);
                  }
                }}
                label="All Day"
                inlineLabel
              />
            )}
          </div>
        </div>

        {/* DATE + TIME */}
        {data.all_day ? (
          <div className="date-time-grid all-day-grid">
            <div className="date-time-column">
              <label>Start Date</label>
              <input
                type="date"
                className="compact-date-input"
                value={getDatePart(data.start)}
                onChange={(e) => {
                  const newDate = e.target.value;
                  // Preserve event duration when changing start date, using local date formatting
                  // to avoid UTC day-shift bugs from toISOString().
                  const oldStart = new Date(`${data.start}T00:00:00`);
                  const oldEnd = new Date(`${data.end}T00:00:00`);
                  const durationDays = Math.max(
                    0,
                    Math.floor((oldEnd - oldStart) / (1000 * 60 * 60 * 24))
                  );
                  const newStart = new Date(`${newDate}T00:00:00`);
                  const newEnd = new Date(newStart);
                  newEnd.setDate(newEnd.getDate() + durationDays);
                  const endDate = `${newEnd.getFullYear()}-${String(newEnd.getMonth() + 1).padStart(
                    2,
                    "0"
                  )}-${String(newEnd.getDate()).padStart(2, "0")}`;
                  handleChange("start", newDate);
                  handleChange("end", endDate);
                }}
              />
              <div className="date-field-hint">{formatDateWithWeekday(data.start)}</div>
            </div>

            <div className="date-time-column">
              <label>End Date</label>
              <input
                type="date"
                className="compact-date-input"
                value={getDatePart(data.end)}
                min={getDatePart(data.start)}
                onChange={(e) => handleChange("end", e.target.value)}
              />
              <div className="date-field-hint">{formatDateWithWeekday(data.end)}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="event-dt-card">
              <div className="event-dt-row">
                <div className="event-dt-row-label">Starts</div>
                <div className="event-dt-row-controls">
                  <button
                    type="button"
                    className="event-dt-pill"
                    onClick={() => toggleDateTimePanel("start-date")}
                  >
                    {formatWeekdayLabel(data.start, effectiveTimeFormat)}
                  </button>
                  <button
                    type="button"
                    className="event-dt-pill"
                    onClick={() => toggleDateTimePanel("start-date")}
                  >
                    {formatDateButtonLabel(data.start)}
                  </button>
                  <button
                    type="button"
                    className="event-dt-pill"
                    onClick={() => toggleDateTimePanel("start-time")}
                  >
                    {formatTimeForDisplay(typedStart || getTimePart(data.start), effectiveTimeFormat)}
                  </button>
                </div>
              </div>

              {activeDateTimePanel === "start-date" && (
                <div className="event-dt-panel">
                  {renderInlineCalendar({
                    selectedDate: getDatePart(data.start),
                    visibleMonth: startCalendarMonth,
                    onVisibleMonthChange: setStartCalendarMonth,
                    onSelectDate: (dateOnly) => {
                      applyEventStartDate(dateOnly);
                      setActiveDateTimePanel(null);
                    },
                  })}
                </div>
              )}

              {activeDateTimePanel === "start-time" && (
                <div className="event-dt-panel">
                  {renderTimeSelects(typedStart || getTimePart(data.start), handleStartTimeChange, effectiveTimeFormat)}
                </div>
              )}

              <div className="event-dt-row">
                <div className="event-dt-row-label">Ends</div>
                <div className="event-dt-row-controls">
                  <button
                    type="button"
                    className="event-dt-pill"
                    onClick={() => toggleDateTimePanel("end-date")}
                  >
                    {formatWeekdayLabel(data.end, effectiveTimeFormat)}
                  </button>
                  <button
                    type="button"
                    className="event-dt-pill"
                    onClick={() => toggleDateTimePanel("end-date")}
                  >
                    {formatDateButtonLabel(data.end)}
                  </button>
                  <button
                    type="button"
                    className="event-dt-pill"
                    onClick={() => toggleDateTimePanel("end-time")}
                  >
                    {formatTimeForDisplay(typedEnd || getTimePart(data.end), effectiveTimeFormat)}
                  </button>
                </div>
              </div>

              {activeDateTimePanel === "end-date" && (
                <div className="event-dt-panel">
                  {renderInlineCalendar({
                    selectedDate: getDatePart(data.end),
                    visibleMonth: endCalendarMonth,
                    onVisibleMonthChange: setEndCalendarMonth,
                    minDate: getDatePart(data.start),
                    onSelectDate: (dateOnly) => {
                      const nextTime = typedEnd || getTimePart(data.end) || "10:00";
                      handleChange("end", `${dateOnly}T${nextTime}`);
                      setActiveDateTimePanel(null);
                    },
                  })}
                </div>
              )}

              {activeDateTimePanel === "end-time" && (
                <div className="event-dt-panel">
                  {renderTimeSelects(typedEnd || getTimePart(data.end), handleEndTimeChange, effectiveTimeFormat)}
                </div>
              )}
            </div>

            <div className="duration-summary-row">
              <span
                className="duration-summary"
                style={{ opacity: 0.7, fontSize: "0.9em" }}
              >
                {getDurationSummary()}
              </span>
            </div>

            <DurationPresets
              startTime={typedStart}
              onChangeEndTime={(newHHmm) => {
                setTypedEnd(newHHmm);
                const date = getDatePart(data.end);
                handleChange("end", `${date}T${newHHmm}`);
              }}
            />
            {timesInvalid && (
              <div className="error-text">End time must be after start time</div>
            )}
          </>
        )}

        <div className="repeat-toggle-row">
          <Toggle
            checked={repeatEnabled}
            onChange={(e, checked) => handleRepeatToggle(checked === true)}
            label="Repeat event"
            inlineLabel
          />
        </div>

        {repeatEnabled && (
          <div className="repeat-summary-row">
            <div className="reminder-summary-text">{repeatSummaryText}</div>
            <button
              type="button"
              className="repeat-manage-btn"
              onClick={() => {
                setIsRepeatManagerOpen(true);
                setActiveDateTimePanel(null);
              }}
            >
              Manage repeat
            </button>
          </div>
        )}

        {repeatEnabled && isRepeatManagerOpen
          ? createPortal(
              <div
                className="repeat-manager-overlay"
                onClick={() => {
                  setIsRepeatManagerOpen(false);
                  setActiveDateTimePanel(null);
                }}
              >
                <div className="repeat-manager-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="repeat-manager-header">
                    <h3>Repeat event</h3>
                    <button
                      type="button"
                      className="repeat-manager-close-btn"
                      onClick={() => {
                        setIsRepeatManagerOpen(false);
                        setActiveDateTimePanel(null);
                      }}
                    >
                      Done
                    </button>
                  </div>

                  <div className="repeat-controls-grid">
                    <div className="repeat-controls-field">
                      <label>Pattern</label>
                      <select
                        value={repeatPattern}
                        onChange={(e) => {
                          const nextPattern = e.target.value;
                          handleChange("repeat_rule", nextPattern);
                          const normalizedStart = normalizedPatternStartDateOnly(getDatePart(data.start), nextPattern);
                          if (normalizedStart && normalizedStart !== getDatePart(data.start)) {
                            applyEventStartDate(normalizedStart);
                          }
                        }}
                      >
                        {REPEAT_PATTERN_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    {repeatPattern === "weekly_specific_day" ? (
                      <div className="repeat-controls-field">
                        <label>Day</label>
                        <select
                          value={String(repeatWeekday)}
                          onChange={(e) => handleRepeatWeekdayChange(e.target.value)}
                        >
                          {WEEKDAY_OPTIONS.map((option) => (
                            <option key={String(option.value)} value={String(option.value)}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    ) : null}

                    <div className="repeat-controls-field">
                      <label>Start Date</label>
                      <button
                        type="button"
                        className="event-dt-pill"
                        onClick={() => toggleDateTimePanel("repeat-start-date")}
                      >
                        {formatDateButtonLabel(repeatStartDateOnly)}
                      </button>
                    </div>

                    <div className="repeat-controls-field">
                      <label>Ends by</label>
                      <select
                        value={repeatEndMode}
                        onChange={(e) => {
                          const nextMode = e.target.value;
                          handleChange("repeat_end_mode", nextMode);
                          if (nextMode === "forever") {
                            handleChange("repeat_until", "");
                            setActiveDateTimePanel((prev) =>
                              prev === "repeat-until-date" ? null : prev
                            );
                          }
                        }}
                      >
                        {REPEAT_END_MODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>

                    {repeatEndMode === "frequency" ? (
                      <div className="repeat-controls-field">
                        <label>Frequency ({repeatRuleUnit(repeatPattern)})</label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={String(repeatPeriods)}
                          onChange={(e) => {
                            const next = Math.max(1, Number(e.target.value || 1));
                            handleChange("repeat_periods", next);
                          }}
                        />
                      </div>
                    ) : null}

                    {repeatNeedsUntil ? (
                      <div className="repeat-controls-field">
                        <label>Date until</label>
                        <button
                          type="button"
                          className="event-dt-pill"
                          onClick={() => toggleDateTimePanel("repeat-until-date")}
                        >
                          {data.repeat_until ? formatDateButtonLabel(data.repeat_until) : "Select date"}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {activeDateTimePanel === "repeat-start-date" ? (
                    <div className="event-dt-panel">
                      {renderInlineCalendar({
                        selectedDate: repeatStartDateOnly,
                        visibleMonth: startCalendarMonth,
                        onVisibleMonthChange: setStartCalendarMonth,
                        minDate: getDatePart(data.start),
                        onSelectDate: (dateOnly) => {
                          const normalizedStart = normalizedPatternStartDateOnly(dateOnly, repeatPattern);
                          applyEventStartDate(normalizedStart || dateOnly);
                          setActiveDateTimePanel(null);
                        },
                      })}
                    </div>
                  ) : null}

                  {activeDateTimePanel === "repeat-until-date" && repeatNeedsUntil ? (
                    <div className="event-dt-panel">
                      {renderInlineCalendar({
                        selectedDate: data.repeat_until,
                        visibleMonth: repeatUntilCalendarMonth,
                        onVisibleMonthChange: setRepeatUntilCalendarMonth,
                        minDate: getDatePart(data.start),
                        isDateSelectable: isValidRepeatUntilDate,
                        highlightSelectableDates: repeatPattern !== "daily",
                        onSelectDate: (dateOnly) => {
                          if (!isValidRepeatUntilDate(dateOnly)) {
                            window.alert(
                              "Please pick a date that matches the selected repeat pattern and frequency."
                            );
                            return;
                          }
                          handleChange("repeat_until", dateOnly);
                          setActiveDateTimePanel(null);
                        },
                      })}
                    </div>
                  ) : null}

                  {!repeatUntilDateIsValid && data.repeat_until && repeatNeedsUntil ? (
                    <div className="error-text">
                      Please pick a valid date matching this pattern/frequency.
                    </div>
                  ) : null}

                  {repeatEndMode === "frequency" ? (
                    <div className="reminder-summary-text">
                      {repeatCalculatedUntil ? `Ends ${formatDateOnlyForUi(repeatCalculatedUntil)}` : "Ends -"}
                    </div>
                  ) : null}


                </div>
              </div>,
              document.body
            )
          : null}

        <div className="reminder-toggle-row">
          <Toggle
            checked={reminderEnabled}
            onChange={(e, checked) => {
              const enabled = checked === true;
              handleChange("reminder_enabled", enabled);
              if (enabled && !data.reminder_config) {
                handleChange(
                  "reminder_config",
                  JSON.stringify({
                    ...activeReminderConfig,
                    pattern: "one_time_date",
                    one_time_date: activeReminderConfig.reminder_date || "",
                  })
                );
              }
            }}
            label="Reminders"
            inlineLabel
          />
        </div>

        {reminderEnabled ? (
          <div className="reminder-summary-text">{reminderSummary(activeReminderConfig, true)}</div>
        ) : null}

        {reminderEnabled ? (
          <div className="reminder-options-panel">
            <div className="reminder-options-header">Reminder options</div>
            <div className="reminder-options-grid">
              <div className="reminder-options-field">
                <label>Notify</label>
                <select
                  className="reminder-notify-select"
                  value={String(activeReminderConfig.offset_minutes)}
                  onChange={(e) => updateReminderConfig({ offset_minutes: Number(e.target.value) })}
                >
                  {REMINDER_OFFSET_OPTIONS.map((option) => (
                    <option key={option.value} value={String(option.value)}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="reminder-options-field">
                <label>Reminder date</label>
                <input
                  className="reminder-date-input"
                  type="date"
                  value={activeReminderConfig.reminder_date || ""}
                  min={getDatePart(data.start)}
                  onChange={(e) => updateReminderConfig({ reminder_date: e.target.value })}
                />
              </div>
            </div>
          </div>
        ) : null}

        {/* ─────────────────────────────── */}
        <div className="section-divider" />

        <label>Location</label>
        <div className="location-input-wrap">
          <input
            ref={locationInputRef}
            type="text"
            value={data.location || ""}
            onChange={(e) => {
              handleChange("location", e.target.value);
              setShowLocationSuggestions(true);
            }}
            onFocus={() => setShowLocationSuggestions(true)}
            onBlur={() => {
              window.setTimeout(() => setShowLocationSuggestions(false), 140);
            }}
            autoComplete="off"
            placeholder="Address or place name"
          />
          {showLocationSuggestions && !data.is_lesson && (locationLookupBusy || locationSuggestions.length > 0) && (
            <div className="location-suggestions-panel">
              {locationLookupBusy && (
                <div className="location-suggestion location-suggestion--muted">Searching…</div>
              )}
              {!locationLookupBusy && locationSuggestions.map((item) => (
                <button
                  key={item}
                  type="button"
                  className="location-suggestion"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    handleChange("location", item);
                    setShowLocationSuggestions(false);
                  }}
                >
                  {item}
                </button>
              ))}
              {!locationLookupBusy && locationSuggestions.length === 0 && String(data.location || "").trim().length >= 3 && (
                <div className="location-suggestion location-suggestion--muted">No matches found</div>
              )}
            </div>
          )}
        </div>

        <div className="section-divider section-divider-tight" />

        <div className="category-row-label">
          <label>Category</label>
          <button
            type="button"
            className="notes-mode-btn"
            onClick={onOpenCategoryManager}
          >
            Manage
          </button>
        </div>
        <div className="category-select-wrap">
          <select
            value={data.category_id || ""}
            onChange={(e) => {
              const selected = categories.find((item) => item.id === e.target.value);
              if (!selected) {
                handleChange("category_id", "");
                handleChange("category_name", "");
                handleChange("category_color", "");
                return;
              }

              handleChange("category_id", selected.id);
              handleChange("category_name", selected.name || "");
              handleChange("category_color", selected.color || "");
            }}
          >
            <option value="">No category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {data.category_name ? (
            <span className="category-chip" style={{ backgroundColor: data.category_color || "#64748b" }}>
              {data.category_name}
            </span>
          ) : null}
        </div>

        <div className="section-divider section-divider-tight" />

        <div className="attachments-header-row">
          <label>Attachments</label>
          <button type="button" className="notes-mode-btn" onClick={handleChooseAttachments}>
            Upload file
          </button>
        </div>

        <input
          ref={attachmentInputRef}
          type="file"
          multiple
          hidden
          onChange={handleAttachmentPickerChange}
        />

        {((Array.isArray(data.attachments) && data.attachments.length > 0) || pendingFiles.length > 0) ? (
          <div className="attachments-list">
            {data.attachments.map((item, index) => (
              <div className="attachment-row" key={`${index}-${item.storage_path || item.url || "attachment"}`}>
                <div className="attachment-row-main">
                  <div className="attachment-name">{getAttachmentDisplayName(item)}</div>
                  <div className="attachment-meta">
                    {isUploadedAttachment(item)
                      ? [item.content_type, formatFileSize(item.size_bytes)].filter(Boolean).join(" • ") || "Uploaded file"
                      : item.url}
                  </div>
                </div>
                <div className="attachment-actions">
                  <button
                    type="button"
                    className="attachment-open-btn"
                    onClick={() => openAttachment(item)}
                    aria-label="Open attachment"
                  >
                    Open
                  </button>
                  <button
                    type="button"
                    className="attachment-remove-btn"
                    onClick={() => removeAttachment(index)}
                    aria-label="Remove attachment"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}

            {pendingFiles.map((file, index) => (
              <div className="attachment-row attachment-row--pending" key={`${file.name}-${file.lastModified}-${index}`}>
                <div className="attachment-row-main">
                  <div className="attachment-name">{file.name}</div>
                  <div className="attachment-meta">
                    {[file.type || "Pending upload", formatFileSize(file.size)].filter(Boolean).join(" • ")}
                  </div>
                </div>
                <button
                  type="button"
                  className="attachment-remove-btn"
                  onClick={() => removePendingFile(index)}
                  aria-label="Remove pending attachment"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="attachments-empty">No attachments</div>
        )}

        <div className="attachments-help">Files upload after you save the event.</div>

        {/* ─────────────────────────────── */}
        <div className="section-divider" />

        {data.is_lesson && (
          <>
            <label>Invoice Notes</label>
            <textarea
              rows={3}
              value={data.invoice_notes || ""}
              onChange={(e) => handleChange("invoice_notes", e.target.value)}
              className="notes-textarea invoice-notes"
              placeholder="Notes that appear on invoices…"
            />
          </>
        )}

        <div className="notes-label-row">
          <label>Internal Notes</label>
          <button
            type="button"
            className="notes-mode-btn"
            onClick={() => setNotesMode((m) => (m === "edit" ? "preview" : "edit"))}
          >
            {notesMode === "edit" ? "Preview" : "Edit"}
          </button>
        </div>
        {notesMode === "preview" && data.internal_notes ? (
          <div className="notes-preview">
            <ReactMarkdown
              components={{
                a: ({ children, ...props }) => (
                  // eslint-disable-next-line react/jsx-no-target-blank
                  <a {...props} target="_blank" rel="noreferrer">{children}</a>
                ),
              }}
            >
              {linkifyBareUrls(data.internal_notes)}
            </ReactMarkdown>
          </div>
        ) : notesMode === "preview" ? (
          <div className="notes-preview">No notes yet.</div>
        ) : (
          <textarea
            ref={internalNotesRef}
            value={data.internal_notes || ""}
            onChange={(e) => handleChange("internal_notes", e.target.value)}
            className="notes-textarea internal-notes"
            placeholder="Private notes, lesson details, reminders… (supports Markdown)"
          />
        )}

        {/* BUTTONS */}
        <div className="modal-buttons">
          <div className="modal-buttons-left">
            {isEdit && onDelete && (
              <button
                className="delete-btn-sm"
                disabled={isSaving}
                onClick={onDelete}
              >
                Delete
              </button>
            )}
          </div>
          <div className="modal-buttons-right">
            <button
              disabled={isSaving}
              onClick={() => {
                if (isSaving) return;
                setSaveAttempted(false);
                onClose();
              }}
            >
              Cancel
            </button>

            <button
              className="save-btn"
              disabled={isSaving}
              onClick={handlePrimarySave}
            >
              {isSaving ? "Saving…" : isEdit ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventModal;
